// mobile ready code
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    AppBar,
    Toolbar,
    Button,
    useMediaQuery,
    IconButton,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteIcon from '@mui/icons-material/Delete';

// --- Styled Components ---
const StyledAppBar = styled(AppBar)(({ theme }) => ({
    background: "linear-gradient(45deg, #6a1b9a 30%, #ab47bc 90%)",
    boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    color: theme.palette.grey[300],
    "&.Mui-selected": {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.light,
    },
    "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
    },
    textTransform: "none",
    fontSize: "0.9rem",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": { backgroundColor: theme.palette.grey[100] },
    "&:hover": { backgroundColor: theme.palette.grey[200], cursor: "pointer" },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 500,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
}));

const DashboardContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    minHeight: "100vh",
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1.5),
    },
}));

// --- Component ---
const AdminDashboard = () => {
    const [tabValue, setTabValue] = useState(0);
    const [bookings, setBookings] = useState([]);
    const [salons, setSalons] = useState([]);
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [owners, setOwners] = useState([]);
    const [paymentDefaults, setPaymentDefaults] = useState([]);
    const [paymentReceipts, setPaymentReceipts] = useState([]);
    const history = useHistory();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const apptUrl = process.env.REACT_APP_SERVER
        ? 'https://salonserver-k8m8.onrender.com/appointments/all'
        : 'http://localhost:3001/appointments/all';

    //     const salonsUrl = process.env.REACT_APP_SERVER
    //         ? 'https://salonserver-k8m8.onrender.com/salons'
    //         : 'http://localhost:3001/salons';

    //     const salonDelUrl = process.env.REACT_APP_SERVER
    //         ? 'https://salonserver-k8m8.onrender.com/salons/'
    //         : 'http://localhost:3001/salons/';


    const usersUrl = process.env.REACT_APP_SERVER
        ? 'https://salonserver-k8m8.onrender.com/users'
        : 'http://localhost:3001/users';

    //     const userDelUrl = process.env.REACT_APP_SERVER
    //         ? 'https://salonserver-k8m8.onrender.com/users/'
    //         : 'http://localhost:3001/users/';

    //     const signOutUrl = process.env.REACT_APP_SERVER
    //         ? 'https://salonserver-k8m8.onrender.com/logout'
    //         : 'http://localhost:3001/logout';

    const serverUrl = process.env.REACT_APP_SERVER
        ? "https://salonserver-k8m8.onrender.com"
        : "http://localhost:3001";

    const userDelUrl = process.env.REACT_APP_SERVER
        ? 'https://salonserver-k8m8.onrender.com/users/'
        : 'http://localhost:3001/users/';

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchUsers = async () => {
        try {
            const usersResponse = await axios.get(usersUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': ` ${localStorage.getItem("token")} `,
                },
            });
            setUsers(usersResponse.data);
            console.log('Fetched users:', usersResponse.data);

        } catch (error) {
            console.error('Error fetching users:', error.response ? error.response.data : error.message);
        }
    };

    const fetchBookings = async () => {
        try {
            // Fetch all bookings
            const bookingsResponse = await axios.get(apptUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': ` ${localStorage.getItem("token")} `,
                },
            });
            setBookings(bookingsResponse.data);
            console.log('Fetched bookings:', bookingsResponse);

        } catch (error) {
            console.error('Error fetching bookings:', error.response ? error.response.data : error.message);
        }
    }
    // Fetch data on component mount
    useEffect(() => {

        fetchBookings();
        fetchSalons();
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: ` ${localStorage.getItem("token")} `,
                };
                const [bookingsRes, salonsRes, usersRes] = await Promise.all([
                    axios.get(`${serverUrl}/appointments/all`, { headers }),
                    axios.get(`${serverUrl}/salons`, { headers }),
                    axios.get(`${serverUrl}/users`, { headers }),
                ]);
                setBookings(bookingsRes.data);
                setSalons(salonsRes.data);
                const allUsers = usersRes.data;

                const owners = allUsers.filter(
                    (u) => u.usertype === "sprovider"
                );

                const clients = allUsers.filter(
                    (u) => u.usertype === "client"
                );

                setUsers(allUsers);               // optional (if you still need all users)
                setOwners(owners); // new state
                setClients(clients);               // new state
            } catch (err) {
                console.error(err);
            }
        };
        fetchData();
    }, []);

    const handleTabChange = (event, newValue) => setTabValue(newValue);

    const handleLogout = async () => {
        try {
            await axios.delete(`${serverUrl}/logout`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: ` ${localStorage.getItem("token")} `,
                },
            });
        } catch { }
        // localStorage.clear();
        history.push("/admin/login");
    };

    const formatApptTime = (isoTime) => {
        if (!isoTime) return "";
        const date = new Date(isoTime);
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
    };
    const salonDelUrl = process.env.REACT_APP_SERVER
        ? 'https://salonserver-k8m8.onrender.com/salons/'
        : 'http://localhost:3001/salons/';

    const salonsUrl = process.env.REACT_APP_SERVER
        ? 'https://salonserver-k8m8.onrender.com/salons'
        : 'http://localhost:3001/salons';

    const handleDeleteUser = async (id) => {
        const confirmed = window.confirm("Are you sure you want to delete this user?");
        if (!confirmed) return;

        try {
            const response = await axios.delete(
                userDelUrl + id,
                {
                    headers: {
                        Authorization: ` ${localStorage.getItem('token')} `,
                    },
                }
            );
            alert('User deleted successfully');
            fetchUsers()
        } catch (err) {
            alert(err.message);
        }
    }

    const fetchSalons = async () => {
        try {
            // Fetch all salons (enrolled salon owners)
            const salonsResponse = await axios.get(salonsUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': ` ${localStorage.getItem("token")} `,
                },
            });
            setSalons(salonsResponse.data);
            console.log('Fetched salons:', salonsResponse.data);
        } catch (error) {
            console.error('Error fetching salons:', error.response ? error.response.data : error.message);
        }
    }

    const handleDeleteSalon = async (id) => {
        // setSalons(salons.filter(s => s.id !== id));
        const confirmed = window.confirm("Are you sure you want to delete this salon?");
        if (!confirmed) return;

        try {
            const response = await axios.delete(
                salonDelUrl + id,
                {
                    headers: {
                        Authorization: `${localStorage.getItem('token')}`,
                    },
                }
            );
            alert('Salon deleted successfully');
            fetchSalons()
        } catch (err) {
            alert(err.message);
        }
    };



    // const serverUrl = process.env.REACT_APP_SERVER
    //     ? "https://salonserver-k8m8.onrender.com"
    //     : "http://localhost:3001";

    const fetchReport = async () => {
        try {
            const token = localStorage.getItem("token");

            const res = await axios.get(
                `${serverUrl}/admin/reports/salon_subscriptions`,
                {
                    headers: {
                        Authorization: token
                    }
                }
            );

            setRows(res.data);
        } catch (err) {
            console.error("Failed to fetch report", err);
        } finally {
            setLoading(false);
        }
    };


    return (
        <DashboardContainer>
            {/* Header */}
            <StyledAppBar position="static">
                <Toolbar sx={{ flexDirection: isMobile ? "column" : "row" }}>
                    <Typography
                        variant={isMobile ? "subtitle1" : "h6"}
                        sx={{
                            flexGrow: 1,
                            textAlign: "center",
                            color: "white",
                            mb: isMobile ? 1 : 0,
                        }}
                    >
                        GroomWell Admin Dashboard
                    </Typography>
                    <Button
                        color="inherit"
                        onClick={handleLogout}
                        startIcon={<LogoutIcon />}
                        sx={{ textTransform: "none", fontWeight: "bold", fontSize: "0.9rem" }}
                    >
                        Logout
                    </Button>
                </Toolbar>
            </StyledAppBar>

            {/* Tabs */}
            <Box sx={{ p: isMobile ? 1 : 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant={isMobile ? "scrollable" : "standard"}
                    scrollButtons={isMobile ? "auto" : false}
                    sx={{
                        bgcolor: "primary.main",
                        borderRadius: 1,
                        mb: 2,
                    }}
                    centered={!isMobile}
                >

                    <StyledTab label={`Salons (${salons.length})`} />
                    <StyledTab label={`Bookings (${bookings.length})`} />
                    <StyledTab label={`Payment Defaults (${paymentDefaults.length})`} />
                    <StyledTab label={`Payment Receipts (${paymentReceipts.length})`} />
                    <StyledTab label={`Owners (${owners.length})`} />
                    <StyledTab label={`Clients (${clients.length})`} />
                </Tabs>


                {/* Salon Enrollment */}
                {tabValue === 0 && (
                    <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
                            <Table size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "primary.light" }}>
                                        <TableCell><b>Owner ID</b></TableCell>
                                        <TableCell><b>Owner Name</b></TableCell>
                                        <TableCell><b>Owner Mobile</b></TableCell>
                                        <TableCell><b>Owner Email</b></TableCell>

                                        <TableCell><b>Salon ID</b></TableCell>
                                        <TableCell><b>Salon Name</b></TableCell>
                                        <TableCell><b>Salon Mobile</b></TableCell>
                                        <TableCell><b>Salon Email</b></TableCell>

                                        <TableCell><b>Amount</b></TableCell>
                                        <TableCell><b>Paid On</b></TableCell>
                                        <TableCell><b>Valid Till</b></TableCell>
                                    </TableRow>

                                </TableHead>
                                <TableBody>
                                    {rows.length > 0 ? (
                                        rows.map((row, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{row.owner_id}</TableCell>
                                                <TableCell>
                                                    {row.first_name} {row.last_name}
                                                </TableCell>
                                                <TableCell>{row.owner_mobile}</TableCell>
                                                <TableCell>{row.owner_email}</TableCell>

                                                <TableCell>{row.salon_id}</TableCell>
                                                <TableCell>{row.salon_name}</TableCell>
                                                <TableCell>{row.salon_mobile}</TableCell>
                                                <TableCell>{row.salon_email}</TableCell>

                                                <TableCell>₹{row.amount}</TableCell>
                                                <TableCell>
                                                    {new Date(row.paid_at).toLocaleDateString("en-IN")}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(row.end_date).toLocaleDateString("en-IN")}
                                                </TableCell>
                                                <IconButton onClick={() => handleDeleteSalon(row.salon_id)}><DeleteIcon color="error" /></IconButton>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center">
                                                No records found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Salon Enrollment */}
                {/* {tabValue === 0 && (
                    <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
                            <Table size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "primary.light" }}>
                                        <StyledTableCell>ID</StyledTableCell>
                                        <StyledTableCell>Name</StyledTableCell>
                                        <StyledTableCell>Owner</StyledTableCell>
                                        <StyledTableCell>Email</StyledTableCell>
                                        <StyledTableCell>Mobile</StyledTableCell>
                                        <StyledTableCell>Signup Date</StyledTableCell>
                                    </TableRow>

                                </TableHead>
                                <TableBody>
                                    {salons.length > 0 ? (
                                        salons.map((s) => (
                                            <StyledTableRow key={s.id}>
                                                <TableCell>{s.id}</TableCell>
                                                <TableCell>{s.name}</TableCell>
                                                <TableCell>
                                                    {s.user?.first_name} {s.user?.last_name}
                                                </TableCell>
                                                <TableCell>{s.email}</TableCell>
                                                <TableCell>{s.mobile}</TableCell>
                                                <TableCell>{new Date(s.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
                                                <IconButton onClick={() => handleDeleteSalon(s.id)}><DeleteIcon color="error" /></IconButton>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5}>No salons available</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )} */}

                {/* Bookings */}
                {tabValue === 1 && (
                    <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
                            <Table size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "primary.light" }}>
                                        <StyledTableCell>Customer</StyledTableCell>
                                        <StyledTableCell>Mobile</StyledTableCell>
                                        <StyledTableCell>Service</StyledTableCell>
                                        <StyledTableCell>Salon</StyledTableCell>
                                        <StyledTableCell>Date</StyledTableCell>
                                        <StyledTableCell>Time</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                        <StyledTableCell>Rating</StyledTableCell>
                                        <StyledTableCell>Comments</StyledTableCell>

                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bookings.length > 0 ? (
                                        bookings.map((b) => (
                                            <StyledTableRow key={b.id}>
                                                <TableCell>{b.user?.first_name}</TableCell>
                                                <TableCell>{b.user?.mobile}</TableCell>
                                                <TableCell>{b.service}</TableCell>
                                                <TableCell>{b.salon?.name}</TableCell>
                                                <TableCell>
                                                    {new Date(b.apptdate).toLocaleDateString("en-IN")}
                                                </TableCell>
                                                <TableCell>{formatApptTime(b.appttime)}</TableCell>
                                                <TableCell>{b.status}</TableCell>
                                                <TableCell>{b.rating}</TableCell>
                                                <TableCell>{b.feedback}</TableCell>

                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7}>No bookings available</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Owners */}
                {tabValue === 4 && (
                    <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
                            <Table size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "primary.light" }}>





                                        <StyledTableCell>ID</StyledTableCell>
                                        <StyledTableCell>First Name</StyledTableCell>
                                        <StyledTableCell>Last Name</StyledTableCell>
                                        <StyledTableCell>Email</StyledTableCell>
                                        <StyledTableCell>User Type</StyledTableCell>
                                        <StyledTableCell>Mobile</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {owners.length > 0 ? (
                                        owners.map((u) => (
                                            <StyledTableRow key={u.id}>
                                                <TableCell>{u.id}</TableCell>
                                                <TableCell>{u.first_name}</TableCell>
                                                <TableCell>{u.last_name}</TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>{u.usertype}</TableCell>
                                                <TableCell>{u.mobile}</TableCell>
                                                <IconButton onClick={() => handleDeleteUser(u.id)}><DeleteIcon color="error" /></IconButton>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5}>No clients available</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Clients */}
                {tabValue === 4 && (
                    <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
                            <Table size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "primary.light" }}>





                                        <StyledTableCell>ID</StyledTableCell>
                                        <StyledTableCell>First Name</StyledTableCell>
                                        <StyledTableCell>Last Name</StyledTableCell>
                                        <StyledTableCell>Email</StyledTableCell>
                                        <StyledTableCell>User Type</StyledTableCell>
                                        <StyledTableCell>Mobile</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clients.length > 0 ? (
                                        clients.map((u) => (
                                            <StyledTableRow key={u.id}>
                                                <TableCell>{u.id}</TableCell>
                                                <TableCell>{u.first_name}</TableCell>
                                                <TableCell>{u.last_name}</TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>{u.usertype}</TableCell>
                                                <TableCell>{u.mobile}</TableCell>
                                                <IconButton onClick={() => handleDeleteUser(u.id)}><DeleteIcon color="error" /></IconButton>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5}>No clients available</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

            </Box>
        </DashboardContainer>
    );
};

export default AdminDashboard;







// original code
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { useHistory } from 'react-router-dom';
// import {
//     Box,
//     Typography,
//     Tabs,
//     Tab,
//     Table,
//     TableBody,
//     TableCell,
//     TableContainer,
//     TableHead,
//     TableRow,
//     Paper,
//     AppBar,
//     Toolbar,
//     Button,
//     IconButton
// } from '@mui/material';
// import { styled } from '@mui/material/styles';
// import LogoutIcon from '@mui/icons-material/Logout';

// import DeleteIcon from '@mui/icons-material/Delete';

// // Styled components for custom design
// const StyledAppBar = styled(AppBar)(({ theme }) => ({
//     background: 'linear-gradient(45deg, #6a1b9a 30%, #ab47bc 90%)',
//     boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
// }));

// const StyledTab = styled(Tab)(({ theme }) => ({
//     color: theme.palette.grey[300],
//     '&.Mui-selected': {
//         color: theme.palette.common.white,
//         backgroundColor: theme.palette.primary.light,
//     },
//     '&:hover': {
//         backgroundColor: theme.palette.primary.dark,
//         color: theme.palette.common.white,
//     },
// }));

// const StyledTableRow = styled(TableRow)(({ theme }) => ({
//     '&:nth-of-type(odd)': {
//         backgroundColor: theme.palette.grey[100],
//     },
//     '&:hover': {
//         backgroundColor: theme.palette.grey[200],
//         cursor: 'pointer',
//     },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//     fontWeight: 500,
//     color: theme.palette.text.primary,
// }));

// const DashboardContainer = styled(Box)(({ theme }) => ({
//     backgroundColor: theme.palette.grey[50],
//     minHeight: '100vh',
//     padding: theme.spacing(4),
//     boxShadow: '0 0 20px rgba(0,0,0,0.05)',
//     borderRadius: theme.spacing(2),
// }));

// const AdminDashboard = () => {
//     const [tabValue, setTabValue] = useState(0);
//     const [bookings, setBookings] = useState([]);
//     const [salons, setSalons] = useState([]);
//     const [users, setUsers] = useState([]);
//     const [clients, setClients] = useState([]);
//     const [owners, setOwners] = useState([]);
//     const [admins, setAdmins] = useState([]);

//     const [paymentDefaults, setPaymentDefaults] = useState([]);
//     const [paymentReceipts, setPaymentReceipts] = useState([]);
//     const [customers, setCustomers] = useState({});
//     const history = useHistory();

//     const apptUrl = process.env.REACT_APP_SERVER
//         ? 'https://salonserver-k8m8.onrender.com/appointments/all'
//         : 'http://localhost:3001/appointments/all';

//     const salonsUrl = process.env.REACT_APP_SERVER
//         ? 'https://salonserver-k8m8.onrender.com/salons'
//         : 'http://localhost:3001/salons';

//     const salonDelUrl = process.env.REACT_APP_SERVER
//         ? 'https://salonserver-k8m8.onrender.com/salons/'
//         : 'http://localhost:3001/salons/';


//     const usersUrl = process.env.REACT_APP_SERVER
//         ? 'https://salonserver-k8m8.onrender.com/users'
//         : 'http://localhost:3001/users';

//     const userDelUrl = process.env.REACT_APP_SERVER
//         ? 'https://salonserver-k8m8.onrender.com/users/'
//         : 'http://localhost:3001/users/';

//     const signOutUrl = process.env.REACT_APP_SERVER
//         ? 'https://salonserver-k8m8.onrender.com/logout'
//         : 'http://localhost:3001/logout';

//     const fetchBookings = async () => {
//         try {
//             // Fetch all bookings
//             const bookingsResponse = await axios.get(apptUrl, {
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'Authorization': ` ${localStorage.getItem("token")} `,
//                 },
//             });
//             setBookings(bookingsResponse.data);
//             console.log('Fetched bookings:', bookingsResponse);

//         } catch (error) {
//             console.error('Error fetching bookings:', error.response ? error.response.data : error.message);
//         }
//     }
//     const fetchSalons = async () => {
//         try {
//             // Fetch all salons (enrolled salon owners)
//             const salonsResponse = await axios.get(salonsUrl, {
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'Authorization': ` ${localStorage.getItem("token")} `,
//                 },
//             });
//             setSalons(salonsResponse.data);
//             console.log('Fetched salons:', salonsResponse.data);
//         } catch (error) {
//             console.error('Error fetching salons:', error.response ? error.response.data : error.message);
//         }
//     }
//     const fetchUsers = async () => {
//         try {
//             const usersResponse = await axios.get(usersUrl, {
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'Authorization': ` ${localStorage.getItem("token")} `,
//                 },
//             });
//             setUsers(usersResponse.data);
//             console.log('Fetched users:', usersResponse.data);

//         } catch (error) {
//             console.error('Error fetching users:', error.response ? error.response.data : error.message);
//         }
//     };
//     // Fetch data on component mount
//     useEffect(() => {

//         fetchBookings();
//         fetchSalons();
//         fetchUsers();
//     }, []);


//     const handleTabChange = (event, newValue) => {
//         setTabValue(newValue);
//     };

//     const handleDeleteSalon = async (id) => {
//         // setSalons(salons.filter(s => s.id !== id));
//         const confirmed = window.confirm("Are you sure you want to delete this salon?");
//         if (!confirmed) return;

//         try {
//             const response = await axios.delete(
//                 salonDelUrl + id,
//                 {
//                     headers: {
//                         Authorization: ` ${localStorage.getItem('token')} `,
//                     },
//                 }
//             );
//             alert('Salon deleted successfully');
//             fetchSalons()
//         } catch (err) {
//             alert(err.message);
//         }
//     };

//     const handleDeleteUser = async (id) => {
//         const confirmed = window.confirm("Are you sure you want to delete this user?");
//         if (!confirmed) return;

//         try {
//             const response = await axios.delete(
//                 userDelUrl + id,
//                 {
//                     headers: {
//                         Authorization: ` ${localStorage.getItem('token')} `,
//                     },
//                 }
//             );
//             alert('User deleted successfully');
//             fetchUsers()
//         } catch (err) {
//             alert(err.message);
//         }
//     }

//     // const handleDeleteUser = (id) => {
//     //     setUsers(users.filter(s => s.id !== id));
//     // };

//     const handleLogout = async () => {
//         try {
//             // Send sign-out request to Rails Devise endpoint
//             await axios.delete(signOutUrl, {
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'Accept': 'application/json',
//                     'Authorization': ` ${localStorage.getItem("token")} `,
//                 },
//             });
//             // Clear local storage
//             localStorage.removeItem('user');
//             localStorage.removeItem('token');
//             // Redirect to admin sign-in page
//             history.push('/admin/login');
//         } catch (error) {
//             console.error('Logout error:', error.response ? error.response.data : error.message);
//             // Even if the request fails, clear local storage and redirect
//             localStorage.removeItem('user');
//             localStorage.removeItem('token');
//             history.push('/admin/login');
//         }
//     };

//     // Helper to format appttime
//     const formatApptTime = (isoTime) => {
//         if (!isoTime) return '';
//         const date = new Date(isoTime);
//         if (isNaN(date.getTime())) return '';
//         const hours = String(date.getHours()).padStart(2, '0');
//         const minutes = String(date.getMinutes()).padStart(2, '0');
//         return `${hours}:${minutes}`;
//     };

//     return (
//         <DashboardContainer>
//             {/* Header with logout link */}
//             <StyledAppBar position="static">
//                 <Toolbar>
//                     <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', color: 'white' }}>
//                         GroomWell Admin Dashboard
//                     </Typography>
//                     <Button
//                         color="inherit"
//                         onClick={handleLogout}
//                         startIcon={<LogoutIcon />}
//                         sx={{ textTransform: 'none', fontWeight: 'bold' }}
//                     >
//                         Logout
//                     </Button>
//                 </Toolbar>
//             </StyledAppBar>

//             {/* Main content */}
//             <Box sx={{ p: 3 }}>
//                 <Tabs
//                     value={tabValue}
//                     onChange={handleTabChange}
//                     sx={{
//                         bgcolor: 'primary.main',
//                         borderRadius: 1,
//                         mb: 2,
//                     }}
//                     centered
//                 >
//                     <StyledTab label={`Bookings(${bookings.length})`} />
//                     <StyledTab label={`Salon Enrollment(${salons.length})`} />
//                     <StyledTab label={`Payment Defaults(${paymentDefaults.length})`} />
//                     <StyledTab label={`Payment Receipts(${paymentReceipts.length})`} />
//                     <StyledTab label={`Clients(${users.length})`} />
//                 </Tabs>

//                 {/* Bookings Tab */}
//                 {tabValue === 0 && (
//                     <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 3 }}>
//                         <Table>
//                             <TableHead>
//                                 <TableRow sx={{ bgcolor: 'primary.light' }}>
//                                     <StyledTableCell>Customer Name</StyledTableCell>
//                                     <StyledTableCell>Mobile</StyledTableCell>
//                                     <StyledTableCell>Service</StyledTableCell>
//                                     <StyledTableCell>Salon</StyledTableCell>
//                                     <StyledTableCell>Date</StyledTableCell>
//                                     <StyledTableCell>Time</StyledTableCell>
//                                     <StyledTableCell>Status</StyledTableCell>
//                                     <StyledTableCell>Rating</StyledTableCell>
//                                     <StyledTableCell>Comments</StyledTableCell>
//                                 </TableRow>
//                             </TableHead>
//                             <TableBody>
//                                 {bookings.length > 0 ? (
//                                     bookings.map((booking) => {
//                                         // const customer = customers[booking.user_id] || { customerName: 'Loading...', customerMobile: 'Loading...' };
//                                         return (
//                                             <StyledTableRow key={booking.id}>
//                                                 <TableCell>{booking.user.first_name + ' ' + booking.user.last_name}</TableCell>
//                                                 <TableCell>{booking.user.mobile}</TableCell>
//                                                 <TableCell>{booking.service}</TableCell>
//                                                 <TableCell>{booking.salon.name}</TableCell>
//                                                 <TableCell>{new Date(booking.apptdate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
//                                                 <TableCell>{formatApptTime(booking.appttime)}</TableCell>
//                                                 <TableCell>{booking.status}</TableCell>
//                                                 <TableCell>{booking.rating}</TableCell>
//                                                 <TableCell>{booking.feedback}</TableCell>
//                                             </StyledTableRow>
//                                         );
//                                     })
//                                 ) : (
//                                     <StyledTableRow>
//                                         <TableCell colSpan={7}>No bookings available</TableCell>
//                                     </StyledTableRow>
//                                 )}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}

//                 {/* Salon Enrollment Tab */}
//                 {tabValue === 1 && (
//                     <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 3 }}>
//                         <Table>
//                             <TableHead>
//                                 <TableRow sx={{ bgcolor: 'primary.light' }}>
//                                     <StyledTableCell>Salon ID</StyledTableCell>
//                                     <StyledTableCell>Salon Name</StyledTableCell>
//                                     <StyledTableCell>Owner Name</StyledTableCell>
//                                     <StyledTableCell>Email</StyledTableCell>
//                                     <StyledTableCell>Mobile</StyledTableCell>
//                                     <StyledTableCell>Signup Date</StyledTableCell>
//                                 </TableRow>
//                             </TableHead>
//                             <TableBody>
//                                 {salons.length > 0 ? (
//                                     salons.map((salon) => (
//                                         <StyledTableRow key={salon.salon_id}>
//                                             <TableCell>{salon.id}</TableCell>
//                                             <TableCell>{salon.name}</TableCell>
//                                             <TableCell>{salon.user.first_name + ' ' + salon.user.last_name}</TableCell>
//                                             <TableCell>{salon.email}</TableCell>
//                                             <TableCell>{salon.mobile}</TableCell>
//                                             <TableCell>{new Date(salon.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
//                                             <IconButton onClick={() => handleDeleteSalon(salon.id)}><DeleteIcon color="error" /></IconButton>
//                                         </StyledTableRow>
//                                     ))
//                                 ) : (
//                                     <StyledTableRow>
//                                         <TableCell colSpan={5}>No salons enrolled</TableCell>
//                                     </StyledTableRow>
//                                 )}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}

//                 {/* Payment Defaults Tab */}
//                 {tabValue === 2 && (
//                     <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 3 }}>
//                         <Table>
//                             <TableHead>
//                                 <TableRow sx={{ bgcolor: 'primary.light' }}>
//                                     <StyledTableCell>Salon ID</StyledTableCell>
//                                     <StyledTableCell>Owner Name</StyledTableCell>
//                                     <StyledTableCell>Amount Due</StyledTableCell>
//                                     <StyledTableCell>Due Date</StyledTableCell>
//                                 </TableRow>
//                             </TableHead>
//                             <TableBody>
//                                 {paymentDefaults.length > 0 ? (
//                                     paymentDefaults.map((def) => (
//                                         <StyledTableRow key={def.salon_id}>
//                                             <TableCell>{def.salon_id}</TableCell>
//                                             <TableCell>{def.owner_name}</TableCell>
//                                             <TableCell>₹{def.amount_due}</TableCell>
//                                             <TableCell>{new Date(def.due_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
//                                         </StyledTableRow>
//                                     ))
//                                 ) : (
//                                     <StyledTableRow>
//                                         <TableCell colSpan={4}>No payment defaults</TableCell>
//                                     </StyledTableRow>
//                                 )}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}

//                 {/* Payment Receipts Tab */}
//                 {tabValue === 3 && (
//                     <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 3 }}>
//                         <Table>
//                             <TableHead>
//                                 <TableRow sx={{ bgcolor: 'primary.light' }}>
//                                     <StyledTableCell>Receipt ID</StyledTableCell>
//                                     <StyledTableCell>Salon ID</StyledTableCell>
//                                     <StyledTableCell>Owner Name</StyledTableCell>
//                                     <StyledTableCell>Amount Paid</StyledTableCell>
//                                     <StyledTableCell>Payment Date</StyledTableCell>
//                                 </TableRow>
//                             </TableHead>
//                             <TableBody>
//                                 {paymentReceipts.length > 0 ? (
//                                     paymentReceipts.map((receipt) => (
//                                         <StyledTableRow key={receipt.receipt_id}>
//                                             <TableCell>{receipt.receipt_id}</TableCell>
//                                             <TableCell>{receipt.salon_id}</TableCell>
//                                             <TableCell>{receipt.owner_name}</TableCell>
//                                             <TableCell>₹{receipt.amount_paid}</TableCell>
//                                             <TableCell>{new Date(receipt.payment_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
//                                         </StyledTableRow>
//                                     ))
//                                 ) : (
//                                     <StyledTableRow>
//                                         <TableCell colSpan={5}>No payment receipts</TableCell>
//                                     </StyledTableRow>
//                                 )}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}
//                 {/* Payment Receipts Tab */}
//                 {tabValue === 4 && (
//                     <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 3 }}>
//                         <Table>
//                             <TableHead>
//                                 <TableRow sx={{ bgcolor: 'primary.light' }}>
//                                     <StyledTableCell> Id</StyledTableCell>
//                                     <StyledTableCell>Email</StyledTableCell>
//                                     <StyledTableCell> Name</StyledTableCell>
//                                     <StyledTableCell>User Type</StyledTableCell>
//                                     {/* <StyledTableCell>Payment Date</StyledTableCell> */}
//                                 </TableRow>
//                             </TableHead>
//                             <TableBody>
//                                 {users.length > 0 ? (
//                                     users.map((user) => (
//                                         <StyledTableRow key={user.id}>
//                                             <TableCell>{user.id}</TableCell>
//                                             <TableCell>{user.email}</TableCell>
//                                             <TableCell>{user.first_name + ' ' + user.last_name}</TableCell>
//                                             <TableCell>{user.usertype}</TableCell>
//                                             <IconButton onClick={() => handleDeleteUser(user.id)}><DeleteIcon color="error" /></IconButton>

//                                             {/* <TableCell>{new Date(receipt.payment_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell> */}
//                                         </StyledTableRow>
//                                     ))
//                                 ) : (
//                                     <StyledTableRow>
//                                         <TableCell colSpan={5}>No payment receipts</TableCell>
//                                     </StyledTableRow>
//                                 )}
//                             </TableBody>
//                         </Table>
//                     </TableContainer>
//                 )}
//             </Box>
//         </DashboardContainer>
//     );
// };

// export default AdminDashboard;


